import React, { useMemo } from 'react';
import toast from 'react-hot-toast';
import { Box } from '@mui/material';
import {
  FormDialog,
  RhfFractionField,
  RhfSelect,
  RhfTextField,
} from '@core/components';
import type { CreateSizeData, Size } from '../../api/size';
import { createSize, updateSize } from '../../api/size';
import {
  SIZE_FIELDS_BY_VESTMENT_CATEGORY,
  SIZE_FIELD_LABELS,
  getSizeVestmentValue,
} from '../utils/size';
import {
  SIZE_VESTMENT_OPTIONS,
  type SizeVestmentValue,
  sizeVestmentsEqual,
} from '../utils/vestment';

interface SizeDialogProps {
  open: boolean;
  onClose: () => void;
  size?: Size | null;
  onSuccess?: () => void;
}

type SizeFormValues = {
  name: string;
  vestment: SizeVestmentValue | '';
  length: string;
  shoulder: string;
  sleeve: string;
  neck: string;
  chest: string;
  waist: string;
  figure: string;
  head: string;
};

const SizeDialog: React.FC<SizeDialogProps> = ({
  open,
  onClose,
  size,
  onSuccess,
}) => {
  const defaultValues = useMemo<SizeFormValues>(
    () => ({
      name: size?.name ?? '',
      vestment: getSizeVestmentValue(size),
      length: String(size?.length ?? ''),
      shoulder: String(size?.shoulder ?? ''),
      sleeve: String(size?.sleeve ?? ''),
      neck: String(size?.neck ?? ''),
      chest: String(size?.chest ?? ''),
      waist: String(size?.waist ?? ''),
      figure: String(size?.figure ?? ''),
      head: String(size?.head ?? ''),
    }),
    [size]
  );

  const onSubmit = async (data: SizeFormValues) => {
    if (!data.vestment) {
      return;
    }
    const { vestmentCategory, vestmentType } = data.vestment;
    const fields = SIZE_FIELDS_BY_VESTMENT_CATEGORY[vestmentCategory] ?? [];
    const payload: CreateSizeData = {
      name: data.name.trim(),
      vestmentCategory,
      vestmentType,
    };
    for (const field of fields) {
      payload[field] = Number(data[field]);
    }
    if (size) {
      await updateSize(size.id, payload);
      toast.success('Size updated successfully');
    } else {
      await createSize(payload);
      toast.success('Size created successfully');
    }
    onSuccess?.();
  };

  return (
    <FormDialog<SizeFormValues>
      open={open}
      onClose={onClose}
      title={size ? 'Edit Size' : 'Add Size'}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitText={size ? 'Update' : 'Add'}
      cancelText="Cancel"
    >
      {({ watch }) => {
        const vestment = watch('vestment');
        const vestmentCategory = vestment ? vestment.vestmentCategory : '';
        const fields = vestmentCategory
          ? (SIZE_FIELDS_BY_VESTMENT_CATEGORY[vestmentCategory] ?? [])
          : [];
        const measurementFields = fields.map((field) => (
          <RhfFractionField<SizeFormValues>
            key={field}
            name={field}
            label={SIZE_FIELD_LABELS[field]}
            required
            sx={{ ...(fields.length <= 2 && { flex: 1 }) }}
          />
        ));

        return (
          <>
            <RhfTextField<SizeFormValues> name="name" label="Name" required />
            <RhfSelect
              name="vestment"
              label="Vestment"
              required
              options={SIZE_VESTMENT_OPTIONS}
              valuesEqual={sizeVestmentsEqual}
            />
            {fields.length <= 2 ? (
              <Box sx={{ display: 'flex', gap: 2 }}>{measurementFields}</Box>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 2,
                }}
              >
                {measurementFields}
              </Box>
            )}
          </>
        );
      }}
    </FormDialog>
  );
};

export default SizeDialog;
